import {
  Button,
  Center,
  Divider,
  Drawer,
  DrawerBody,
  DrawerCloseButton,
  DrawerContent,
  DrawerFooter,
  DrawerHeader,
  DrawerOverlay,
  Heading,
  Spinner,
  Stack,
  Text,
  useDisclosure,
} from "@chakra-ui/react";
import { Form, Formik } from "formik";
import { observer } from "mobx-react-lite";
import React, { useState } from "react";
import { DBAction } from "../../../../enum/DBAction";
import { IOperationRuleAlertUser } from "../../../../stores/data/insightAlerts/insightAlertModel";
import { ILocationMenuInsight } from "../../../../stores/data/operationRuleLocations/OperationRuleLocationModel";
import { useStores } from "../../../../stores/setup/use-store";
import { AlertTemplateModal } from "./AlertTemplateModal";
import { AlertCustomTemplate } from "./AlertCustomTemplate";
import { ChannelMismatchModal } from "./ChannelMismatchModal";
import { LocationDataWarningModal } from "./LocationDataWarningModal";
import SendConfig from "./SendConfig";
import { scrollToFirstError } from "pages/Alert/util/scrollToFirstError";
import { EditAlertFormValues } from "pages/Alert/types";
import { useAlertEditDrawer } from "../hooks";
import { useChannelValidation } from "../hooks/useChannelValidation";
import { useUserSelectionPermissions } from "pages/Alert/hooks";
import { AlertBasicInfo } from "./AlertBasicInfo";
import { AlertDeliveryChannel } from "./AlertDeliveryChannel";
import { AlertUserSelection } from "./AlertUserSelection";
import { AlertEditCondition } from "./AlertEditCondition";
import { GeofenceMapContainer } from "components/Map/GeofenceMapContainer";
import { extractCoordinatesFromGeoJSON } from "pages/ForecastChart/ForecastChartPanelMap/util/extractCoordinatesFromGeoJSON";
import { InsightAlertEditSection } from "./InsightAlertEditSection";
import { SensorAlertSection } from "components/SensorAlertSection/SensorAlertSection";

interface InsightAlertModalProps {
  isOpen: boolean;
  onClose: () => void;
}

const AlertEditDrawer: React.FC<InsightAlertModalProps> = ({
  isOpen,
  onClose,
}) => {
  const {
    t,
    initialValues,
    validationSchema,
    handleOnSaveFormAlertModal,
    resetInitialValues,
    usersTableSelected,
    setUsersTableSelected,
    alertTitle,
    setAlertTitle,
    alertDescription,
    setAlertDescription,
    alertUsers,
    conditionsForExampleTemplate,
    geofence,
    clearGeofence,
    handleGeoJsonLoaded,
    selectedGeofence,
    handleDrawingMode,
    handlePointSelectionMode,
    isLoading,
    isSaving,
    isDetectingLocationType,
    areas,
    handleAreaSelection,
    setIsGeofenceLoadedByGeoJson,
    handleEnableAreaSelector,
    areaSelectorVisible,
    handleLocationTypeSelect,
    handleBack,
    handleBackFromGeofence,
    alertLocationType,
    selectedStationForAlert,
    isLocationWarningModalOpen,
    handleLocationWarningConfirm,
    handleLocationWarningCancel,
    isInsightAlert,
    insightAlertData,
    initialSensorAlertConfig,
    sensorConditions,
    sensorAlertFrequency,
    hasSensorAlertConfig,
  } = useAlertEditDrawer(isOpen, onClose);
  const {
    dataStores: { insightAlertStore, forecastChartPageStore },
  } = useStores();

  const { checkChannelMismatch } = useChannelValidation();
  const { hasSpecialPermission } = useUserSelectionPermissions({});
  const [pendingSubmitValues, setPendingSubmitValues] =
    useState<EditAlertFormValues | null>(null);

  const {
    isOpen: isAlertTemplateOpen,
    onOpen: onAlertTemplateOpen,
    onClose: onAlertTemplateClose,
  } = useDisclosure();

  const {
    isOpen: isChannelMismatchModalOpen,
    onOpen: onChannelMismatchModalOpen,
    onClose: onChannelMismatchModalClose,
  } = useDisclosure();

  const [mismatchedChannels, setMismatchedChannels] = useState<string[]>([]);

  const handleChannelMismatchConfirm = () => {
    if (pendingSubmitValues) {
      handleOnSaveFormAlertModal(pendingSubmitValues);
      setPendingSubmitValues(null);
    }
    onChannelMismatchModalClose();
  };

  const handleChannelMismatchCancel = () => {
    setPendingSubmitValues(null);
    onChannelMismatchModalClose();
  };

  // Handler for drawer close that preserves data during point/area selection
  const handleDrawerClose = () => {
    const isInSelectionMode =
      forecastChartPageStore.isAreaDrawingMode ||
      forecastChartPageStore.isPointSelectionMode;

    onClose();

    if (!isInSelectionMode) {
      resetInitialValues();
    }
  };

  return (
    <>
      <Drawer
        isOpen={isOpen}
        placement="right"
        onClose={handleDrawerClose}
        size="xl"
      >
        <Formik<EditAlertFormValues>
          initialValues={initialValues}
          validationSchema={validationSchema}
          onSubmit={(values) => {
            const mismatched = checkChannelMismatch(
              insightAlertStore.operationRuleAlertUserSelected,
              values.channels || [],
            );

            if (mismatched.length > 0) {
              setMismatchedChannels(mismatched);
              setPendingSubmitValues(values);
              onChannelMismatchModalOpen();
            } else {
              handleOnSaveFormAlertModal(values);
            }
          }}
          validateOnBlur={false}
          validateOnChange={false}
          enableReinitialize={true}
        >
          {({
            values,
            errors,
            touched,
            handleChange,
            handleSubmit,
            submitForm,
            setFieldValue,
            validateForm,
            setTouched,
          }) => {
            const handleUsersSelectionChange = (
              selectedRowKeys: React.Key[],
              selectedRows: IOperationRuleAlertUser[],
            ) => {
              setFieldValue("selectedUsers", selectedRowKeys);
              setUsersTableSelected(selectedRowKeys);
              insightAlertStore.setOperationRuleAlertUserSelected(selectedRows);
            };

            const handleSubmitWithScroll = async () => {
              if (!isInsightAlert && !alertLocationType) {
                setTouched({ geofence: true, stationRegion: true });
                return;
              }

              const formErrors = await validateForm();

              if (Object.keys(formErrors).length > 0) {
                const allTouched: Record<string, boolean> = {};
                Object.keys(initialValues).forEach((key) => {
                  allTouched[key] = true;
                });

                setTouched(allTouched);

                setTimeout(() => {
                  scrollToFirstError(formErrors, allTouched);
                }, 100);

                return;
              }
              submitForm();
            };

            const handleGeoJsonLoadedWrapper = (geojson: any) => {
              handleGeoJsonLoaded(geojson);

              if (!geojson) return;
              const coordinates = extractCoordinatesFromGeoJSON(geojson);
              setFieldValue("geofence", coordinates);
            };

            return (
              <>
                <AlertTemplateModal
                  isOpen={isAlertTemplateOpen}
                  onClose={onAlertTemplateClose}
                  title={alertTitle}
                  description={alertDescription}
                  conditions={conditionsForExampleTemplate(
                    values.conditionGroups,
                  )}
                  area={values.area}
                />
                <DrawerOverlay />
                <DrawerContent bg="white">
                  <DrawerCloseButton color={"black"} />
                  <DrawerHeader>
                    <Heading
                      display={"flex"}
                      size="md"
                      color="black"
                      gap="16px"
                    >
                      <Text mt={"3px"}>{t("editAlert")}</Text>
                    </Heading>
                  </DrawerHeader>

                  <DrawerBody color="black">
                    {isLoading ? (
                      <Center h="100%">
                        <Spinner
                          speed="0.65s"
                          emptyColor="gray.300"
                          color="newBlue.500"
                          size="xl"
                        />
                      </Center>
                    ) : (
                      <Form onSubmit={handleSubmit}>
                        <Stack spacing={6}>
                          {/* Alert Category - Always insight/event mode (no tabs) */}
                          {isInsightAlert && insightAlertData ? (
                            /* Insight Alert Edit Section */
                            <>
                              <InsightAlertEditSection
                                dashboardId={insightAlertData.dashboardId}
                              dashboardName={insightAlertData.dashboardName}
                              insightId={insightAlertData.insightId}
                              insightName={insightAlertData.insightName}
                              conditions={insightAlertData.conditions}
                              locationName={insightAlertData.locationName}
                              station={insightAlertData.station}
                              geofence={insightAlertData.geofence}
                              t={t}
                              onDashboardChange={(
                                dashboardId: number | undefined,
                              ) => {
                                insightAlertStore.setSelectedDashboardIdForEdit(
                                  dashboardId ?? null,
                                );
                                // Clear insight selection when dashboard changes
                                setFieldValue("insightId", undefined);
                                setFieldValue("locationId", undefined);
                              }}
                              onInsightChange={(
                                insight: ILocationMenuInsight,
                                locationId: number,
                              ) => {
                                // Store the new insight selection for submission
                                setFieldValue("insightId", insight.id);
                                setFieldValue("locationId", locationId);

                                // Use template title based on alert type
                                const TEMPLATE_TITLES = {
                                  extreme_wind: t("extremeWindTitle"),
                                  port_closure: t("portClosureTitle"),
                                  extreme_rain: t("extremeRainTitle"),
                                } as Record<string, string>;
                                const alertType =
                                  values.alertType || "extreme_wind";
                                const templateTitle =
                                  TEMPLATE_TITLES[alertType] ||
                                  TEMPLATE_TITLES["extreme_wind"];
                                setAlertTitle(templateTitle);
                                setFieldValue("customTitle", templateTitle);

                                // Always use translated template description
                                const TEMPLATE_DESCRIPTIONS = {
                                  extreme_wind: t("possibilityOfImpacts"),
                                  port_closure: t("potentialSuspension"),
                                  extreme_rain: t("possibilityOfFlooding"),
                                } as Record<string, string>;
                                const description =
                                  TEMPLATE_DESCRIPTIONS[alertType] ||
                                  TEMPLATE_DESCRIPTIONS["extreme_wind"];
                                setAlertDescription(description);
                                setFieldValue("customDescription", description);
                              }}
                            />
                            {/* Sensor Alert Section — only for insight alerts with sensor config (Phase 10, FR-060) */}
                            {hasSensorAlertConfig && (
                              <SensorAlertSection
                                macroRegionOrigin={insightAlertStore.operationRuleAlert?.macroRegionOrigin}
                                macroRegionId={values.macroRegionId}
                                locationPoint={
                                  insightAlertData.station
                                    ? { lat: insightAlertData.station.lat, lng: insightAlertData.station.lon }
                                    : undefined
                                }
                                geofence={insightAlertData.geofence}
                                setFieldValue={setFieldValue}
                                sensorConditions={values.sensorAlertConfig?.conditions ?? sensorConditions}
                                alertFrequency={values.sensorAlertConfig?.alertFrequency ?? sensorAlertFrequency}
                                errors={errors}
                                initialSensorAlertConfig={initialSensorAlertConfig}
                                editModeAlertId={insightAlertStore.operationRuleAlert?.id}
                                eventName={values.name || insightAlertData.insightName}
                                eventLocationName={insightAlertData.locationName}
                              />
                            )}
                            </>
                          ) : (
                            /* Standalone Alert Edit Sections */
                            <>
                              <AlertBasicInfo
                                t={t}
                                errors={errors}
                                touched={touched}
                                setFieldValue={setFieldValue}
                                handleLocationTypeSelect={
                                  handleLocationTypeSelect
                                }
                                handleBack={handleBack}
                                alertLocationType={alertLocationType}
                                handlePointSelectionMode={
                                  handlePointSelectionMode
                                }
                                selectedStationForAlert={
                                  selectedStationForAlert
                                }
                                isDetectingLocationType={
                                  isDetectingLocationType
                                }
                              />
                              {alertLocationType === "area" && (
                                <GeofenceMapContainer
                                  values={values}
                                  errors={errors}
                                  touched={touched}
                                  setFieldValue={setFieldValue}
                                  selectedGeofence={
                                    selectedGeofence?.length
                                      ? selectedGeofence
                                      : (geofence as any)
                                  }
                                  handleDrawingMode={handleDrawingMode}
                                  handleGeoJsonLoaded={
                                    handleGeoJsonLoadedWrapper
                                  }
                                  t={t}
                                  showGeofenceError={
                                    insightAlertStore.isGeofenceInvalid &&
                                    insightAlertStore.isGeofenceLoadedByGeoJson
                                  }
                                  clearGeofence={() => {
                                    clearGeofence();
                                    setFieldValue("geofence", []);
                                  }}
                                  setIsGeofenceLoadedByGeoJson={
                                    setIsGeofenceLoadedByGeoJson
                                  }
                                  areas={areas}
                                  handleAreaSelection={handleAreaSelection}
                                  handleEnableAreaSelector={
                                    handleEnableAreaSelector
                                  }
                                  areaSelectorVisible={areaSelectorVisible}
                                  hideSaveAreaButtonOnMap={areaSelectorVisible}
                                  onBackToTypeSelection={() =>
                                    handleBackFromGeofence(setFieldValue)
                                  }
                                />
                              )}

                              <AlertEditCondition
                                t={t}
                                conditionGroups={
                                  values.conditionGroups ||
                                  insightAlertStore.operationRuleAlert
                                    ?.conditionGroups ||
                                  null
                                }
                                environmentalVariables={
                                  insightAlertStore.environmentalVariables
                                }
                                setFieldValue={setFieldValue}
                                errors={errors}
                              />
                            </>
                          )}

                          <AlertCustomTemplate
                            t={t}
                            alertTitle={alertTitle}
                            alertDescription={alertDescription}
                            setAlertTitle={setAlertTitle}
                            setAlertDescription={setAlertDescription}
                            handleChange={handleChange}
                            onAlertTemplateOpen={onAlertTemplateOpen}
                            errors={errors}
                            touched={touched}
                          />

                          <SendConfig
                            handleChange={handleChange}
                            values={values}
                            t={t}
                          />

                          <Divider borderColor={"#D4D4D8"} />

                          <AlertDeliveryChannel
                            t={t}
                            values={values}
                            setFieldValue={setFieldValue}
                            errors={errors}
                            touched={touched}
                          />

                          <AlertUserSelection
                            t={t}
                            alertUsers={alertUsers}
                            usersTableSelected={usersTableSelected}
                            setUsersTableSelected={setUsersTableSelected}
                            setFieldValue={setFieldValue}
                            operationRuleAlertUserSelected={
                              insightAlertStore.operationRuleAlertUserSelected
                            }
                            setOperationRuleAlertUserSelected={(users) =>
                              insightAlertStore.setOperationRuleAlertUserSelected(
                                users,
                              )
                            }
                            onUsersSelectionChange={handleUsersSelectionChange}
                            disabled={!hasSpecialPermission}
                          />
                        </Stack>
                      </Form>
                    )}
                  </DrawerBody>
                  <DrawerFooter borderTopWidth="1px" bg="white">
                    <Button
                      variant="ghost"
                      color="newBlue.500"
                      mr={3}
                      onClick={() => {
                        onClose();
                        resetInitialValues();
                      }}
                      isDisabled={isSaving}
                    >
                      {t("cancel")}
                    </Button>
                    <Button
                      layerStyle="ocean"
                      _hover={{ layerStyle: "ocean" }}
                      onClick={handleSubmitWithScroll}
                      isDisabled={
                        insightAlertStore.isGeofenceInvalid || isSaving
                      }
                      isLoading={isSaving}
                      loadingText={t("saving") as string}
                    >
                      {insightAlertStore.operationRuleAlertAction ===
                      DBAction.CREATE
                        ? t("addAlert")
                        : t("save")}
                    </Button>
                  </DrawerFooter>
                </DrawerContent>
              </>
            );
          }}
        </Formik>
      </Drawer>

      <ChannelMismatchModal
        isOpen={isChannelMismatchModalOpen}
        onClose={handleChannelMismatchCancel}
        onConfirm={handleChannelMismatchConfirm}
        t={t}
        mismatchedChannels={mismatchedChannels}
      />

      <LocationDataWarningModal
        isOpen={isLocationWarningModalOpen}
        onClose={handleLocationWarningCancel}
        onConfirm={handleLocationWarningConfirm}
        t={t}
      />
    </>
  );
};

export default observer(AlertEditDrawer);
